import type { Lock } from '@sunucupark/client-core';

/**
 * One tab at a time, using the Web Locks API. Browsers without it (or a page
 * served over plain http in an old browser) fall back to a promise chain, which
 * still serialises the tab itself.
 */
export class WebLock implements Lock {
  private readonly queues = new Map<string, Promise<unknown>>();

  constructor(private readonly prefix = 'postamerkezi') {}

  async run<T>(
    name: string,
    task: () => Promise<T>,
    options: { signal?: AbortSignal } = {},
  ): Promise<T> {
    const key = `${this.prefix}:${name}`;
    const locks = navigator.locks;
    if (!locks) return this.runLocally(key, task);
    return locks.request(key, { signal: options.signal }, () => task());
  }

  private runLocally<T>(key: string, task: () => Promise<T>): Promise<T> {
    const previous = this.queues.get(key) ?? Promise.resolve();
    const result = previous.then(task, task);
    this.queues.set(
      key,
      result.catch(() => undefined),
    );
    return result;
  }
}
